import db from '@/db'
import { ref, watch } from 'vue'
import { useDebounceFn } from '@vueuse/core'
import { extractKanjis } from '@/utils/stringUtils'

export function useSearch() {
  const searchQuery = ref('')
  const searchResults = ref<string[]>([])
  const isLoading = ref(false) // 1. Add loading state

  async function performSearch(query: string) {
    try {
      if (!query) {
        const history = await db.searchHistory.orderBy('createdAt').reverse().limit(3).toArray()
        searchResults.value = history.map((item) => item.kanji)
        return
      }

      const kanjis = await db.kanjis
        .filter((item) => {
          return (
            item.kanji === query ||
            item.translation?.toLowerCase().includes(query.toLowerCase()) ||
            item.bushu === query
          )
        })
        .toArray()

      const readings = await db.readings.filter((item) => item.reading.includes(query)).toArray()

      // Extract kanji from the query and search for them too
      const extractedKanjis = extractKanjis(query)
      let kanjiFromQuery: typeof kanjis = []
      if (extractedKanjis.length > 0) {
        // Search for each kanji character individually
        const kanjiSet = new Set(extractedKanjis.split(''))
        kanjiFromQuery = await db.kanjis
          .filter((item) => kanjiSet.has(item.kanji))
          .toArray()
      }

      const combined = new Set([
        ...kanjis.map((item) => item.kanji),
        ...readings.map((item) => item.kanji),
        ...kanjiFromQuery.map((item) => item.kanji),
      ])

      searchResults.value = Array.from(combined).slice(0, 5)
    } finally {
      // 2. Always turn off loading when the search completes (or errors)
      isLoading.value = false
    }
  }

  const debouncedSearch = useDebounceFn((query: string) => {
    performSearch(query)
  }, 300)

  // 3. Watch for input: Turn on loading instantly, then trigger debounce
  watch(
    searchQuery,
    (newQuery) => {
      isLoading.value = true
      debouncedSearch(newQuery)
    },
    { immediate: true },
  )

  async function saveSearch(kanji: string) {
    await db.searchHistory.put({ kanji, createdAt: new Date() }, kanji)
  }

  // 4. Export the new isLoading ref
  return { searchQuery, searchResults, saveSearch, isLoading }
}
